<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import LiquidityChart from './LiquidityChart.vue'
import LiquidityUpdateModal from './LiquidityUpdateModal.vue'
import ControlBar from './ControlBar.vue'
import { useFormat } from '../composables/useFormat.js'
import {
  LIQUIDITY_COLORS,
  TF_ORDER,
  levelPillText,
} from '../composables/liquidityLevelStyles.js'

const POLL_MS = 60000

const props = defineProps<{
  exchange: string
  refreshMs: number
  apiBase: string
}>()

const apiBase = (props.apiBase || '/api/desk').replace(/\/$/, '')
const { price } = useFormat()

const exchange = props.exchange || 'Binance'
const symbol = ref('BTCUSDT')
const timeframe = ref('1h')
const threshold = ref(0.85)
const loading = ref(false)
const errorMessage = ref('')
const levels = ref<any>(null)
const status = ref<any>(null)
const chartHeight = ref(520)
const showUpdateModal = ref(false)
const updateNoticeKey = ref(0)
const levelsSnapshot = ref('')
const zoneBusy = ref(false)

const ALL_TIMEFRAMES = ['1m', '3m', '5m', '15m', '30m', '1h', '2h', '4h', '1d']
const DEFAULT_SYMBOLS = ['BTCUSDT', 'ETHUSDT']
const TF_ORDER_LIST = TF_ORDER
const tfColors = LIQUIDITY_COLORS

const chartKey = computed(() => `${symbol.value}:${timeframe.value}`)

const coinSymbols = ref<string[]>([])
const availableTargets = computed(() => status.value?.targets ?? [])

const availableSymbols = computed(() => {
  const seen = new Set<string>()
  const out: string[] = []
  for (const sym of [...coinSymbols.value, ...availableTargets.value.map((t: any) => t.symbol)]) {
    if (sym && !seen.has(sym)) {
      seen.add(sym)
      out.push(sym)
    }
  }
  return out.length ? out : DEFAULT_SYMBOLS
})

function normalizeSymbol(value: string): string {
  const sym = value.trim().toUpperCase()
  if (!sym) return 'BTCUSDT'
  return sym.endsWith('USDT') ? sym : `${sym}USDT`
}

function readQuery() {
  const params = new URLSearchParams(window.location.search)
  const sym = params.get('symbol')
  const tf = params.get('timeframe')
  if (sym) symbol.value = normalizeSymbol(sym)
  if (tf && ALL_TIMEFRAMES.includes(tf)) timeframe.value = tf
}

function writeQuery() {
  const params = new URLSearchParams(window.location.search)
  params.set('symbol', symbol.value)
  params.set('timeframe', timeframe.value)
  const qs = params.toString()
  const next = `${window.location.pathname}?${qs}`
  const current = `${window.location.pathname}${window.location.search}`
  if (current !== next) {
    window.history.replaceState(window.history.state, '', next)
  }
}

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
}

async function fetchJson(url: string, init: RequestInit = {}) {
  const res = await fetch(url, {
    credentials: 'same-origin',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.headers || {}),
    },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    const err: any = new Error(body.message || body.detail || res.statusText)
    err.status = res.status
    throw err
  }
  return res.json()
}

function applyLevelsPayload(next: any) {
  levels.value = next
  levelsSnapshot.value = levelsFingerprint(next)
}

async function loadCoins() {
  try {
    const data = await fetchJson(`${apiBase}/coins`)
    coinSymbols.value = Array.isArray(data.symbols) ? data.symbols : []
  } catch {
    coinSymbols.value = []
  }
}

let loadSeq = 0

async function loadLevels() {
  return fetchJson(
    `${apiBase}/${exchange}/${symbol.value}/zones?threshold=${threshold.value}`,
  )
}

function levelsFingerprint(data: any): string {
  if (!data?.timeframes) return ''
  const parts: string[] = [
    String(data.captured_at ?? ''),
    String(data.reference_price ?? ''),
  ]

  for (const tf of TF_ORDER_LIST) {
    const frame = data.timeframes[tf]
    if (!frame) continue
    for (const side of ['longs', 'shorts'] as const) {
      const prices = (frame[side] || [])
        .map((level: { price: number; blocked?: boolean }) => {
          const flag = level.blocked ? '1' : '0'
          return `${Number(level.price).toFixed(2)}:${flag}`
        })
        .sort()
        .join(',')
      parts.push(`${tf}:${side}:${prices}`)
    }
  }

  return parts.join('|')
}

function pageIsVisible(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'visible'
}

function notifyLevelsUpdated() {
  if (!pageIsVisible()) return
  updateNoticeKey.value += 1
  showUpdateModal.value = true
}

type ReloadOptions = {
  silent?: boolean
  notifyOnChange?: boolean
}

async function reloadLevels(options: ReloadOptions = {}) {
  const { silent = false, notifyOnChange = false } = options
  const seq = ++loadSeq
  if (!silent) {
    loading.value = true
  }
  errorMessage.value = ''
  const previousSnapshot = levelsSnapshot.value

  try {
    const next = await loadLevels()
    if (seq !== loadSeq) return

    const nextSnapshot = levelsFingerprint(next)
    const hadSnapshot = previousSnapshot !== ''
    const changed = hadSnapshot && nextSnapshot !== previousSnapshot

    applyLevelsPayload(next)

    if (notifyOnChange && changed) {
      notifyLevelsUpdated()
    }
  } catch (error: any) {
    if (seq !== loadSeq) return
    levels.value = null
    levelsSnapshot.value = ''
    if (error?.status === 404) {
      errorMessage.value = `No zone data for ${symbol.value}.`
    } else {
      errorMessage.value = error?.message || 'Request failed'
    }
  } finally {
    if (seq === loadSeq && !silent) {
      loading.value = false
    }
  }
}

async function load() {
  try {
    status.value = await fetchJson(`${apiBase}/status`)
  } catch (error: any) {
    errorMessage.value = error?.message || 'Request failed'
  }
  await reloadLevels()
}

function measureChart() {
  const w = window.innerWidth
  const h = window.innerHeight
  chartHeight.value = w < 560
    ? Math.round(Math.min(480, Math.max(300, h * 0.52)))
    : w < 940 ? 440 : 560
}

const levelRows = computed(() => {
  const data = levels.value?.timeframes
  if (!data) return []

  const rows: Array<{
    side: string
    price: number
    timeframe: string
    pct_of_max: number
    distance_pct: number
    blocked?: boolean
    block_id?: number | null
  }> = []

  for (const tf of TF_ORDER_LIST) {
    const frame = data[tf]
    if (!frame) continue
    for (const level of frame.longs || []) {
      rows.push({ ...level, side: 'long', timeframe: tf })
    }
    for (const level of frame.shorts || []) {
      rows.push({ ...level, side: 'short', timeframe: tf })
    }
  }

  return rows.sort((a, b) => b.price - a.price)
})

function formatDistance(pct: number) {
  const sign = pct >= 0 ? '+' : ''
  return `${sign}${pct.toFixed(2)}%`
}

async function postZone(action: 'block' | 'unblock', payload: Record<string, unknown>) {
  if (zoneBusy.value) return
  zoneBusy.value = true
  errorMessage.value = ''
  try {
    const next = await fetchJson(
      `${apiBase}/${exchange}/${symbol.value}/zones/${action}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          ...payload,
          threshold: threshold.value,
        }),
      },
    )
    applyLevelsPayload(next)
  } catch (error: any) {
    errorMessage.value = error?.message || 'Request failed'
  } finally {
    zoneBusy.value = false
  }
}

function blockZone(row: { timeframe: string; side: string; price: number }) {
  return postZone('block', {
    timeframe: row.timeframe,
    side: row.side,
    price: row.price,
  })
}

function unblockZone(row: { block_id?: number | null; timeframe: string; side: string; price: number }) {
  return postZone('unblock', {
    block_id: row.block_id || undefined,
    timeframe: row.timeframe,
    side: row.side,
    price: row.price,
  })
}

function onSymbolChange(next: string) {
  symbol.value = normalizeSymbol(next)
  writeQuery()
  levelsSnapshot.value = ''
  reloadLevels()
}

function onTimeframeChange(next: string) {
  timeframe.value = next
  writeQuery()
}

watch(threshold, () => {
  levelsSnapshot.value = ''
  reloadLevels()
})

function pollLevels() {
  if (!pageIsVisible()) return
  reloadLevels({ silent: true, notifyOnChange: true })
}

let poller: ReturnType<typeof setInterval> | undefined
onMounted(async () => {
  readQuery()
  measureChart()
  window.addEventListener('resize', measureChart, { passive: true })
  await loadCoins()
  await load()
  poller = setInterval(pollLevels, props.refreshMs || POLL_MS)
})
onBeforeUnmount(() => {
  clearInterval(poller)
  window.removeEventListener('resize', measureChart)
})
</script>

<template>
  <div class="liquidity-app min-w-0 space-y-4 md:space-y-5">
    <header class="panel-glass rounded-[1.75rem] p-4 sm:rounded-[2rem] sm:p-5 md:p-6">
      <div class="panel-glass-content flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div class="min-w-0 space-y-2">
          <span class="panel-glass-chip inline-flex rounded-full px-3 py-1 text-xs font-semibold text-zinc-600 dark:text-zinc-300">
            Zones · {{ TF_ORDER_LIST.join(' / ') }}
          </span>
          <div>
            <h1 class="text-lg font-semibold tracking-tight text-zinc-900 dark:text-white sm:text-xl">
              {{ exchange }} · {{ symbol }} · {{ timeframe }}
            </h1>
            <p class="mt-1 text-xs text-zinc-500 dark:text-zinc-400 sm:text-sm">
              <span class="inline-flex flex-wrap items-center gap-x-4 gap-y-1">
                <span
                  v-for="tf in TF_ORDER_LIST"
                  :key="`s-${tf}`"
                  class="inline-flex items-center gap-1.5"
                >
                  <span
                    class="inline-block h-0.5 w-5 rounded-full"
                    :style="{ background: tfColors.short[tf] }"
                  />
                  <span class="text-rose-600">{{ tf }} short</span>
                </span>
                <span
                  v-for="tf in TF_ORDER_LIST"
                  :key="`l-${tf}`"
                  class="inline-flex items-center gap-1.5"
                >
                  <span
                    class="inline-block h-0.5 w-5 rounded-full"
                    :style="{ background: tfColors.long[tf] }"
                  />
                  <span class="text-emerald-600">{{ tf }} long</span>
                </span>
              </span>
            </p>
          </div>
        </div>
        <div
          v-if="levels"
          class="panel-glass-inset shrink-0 rounded-2xl px-4 py-3 text-start sm:text-end"
        >
          <p class="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Reference</p>
          <p class="mt-0.5 font-mono text-xl font-semibold tabular-nums text-zinc-900 dark:text-white sm:text-2xl">
            {{ price(levels.reference_price) }}
          </p>
        </div>
      </div>
    </header>

    <ControlBar
      :symbol="symbol"
      :timeframe="timeframe"
      :threshold="threshold"
      :show-candles="true"
      :hide-candles-toggle="true"
      :hide-refresh="true"
      :timeframes="ALL_TIMEFRAMES"
      :symbols="availableSymbols"
      :loading="loading"
      @update:symbol="onSymbolChange"
      @update:timeframe="onTimeframeChange"
      @update:threshold="threshold = $event"
    />

    <div
      v-if="errorMessage"
      class="panel-glass rounded-2xl border-rose-300/40 p-4 dark:border-rose-400/20"
    >
      <p class="panel-glass-content text-sm text-rose-600 dark:text-rose-300">{{ errorMessage }}</p>
    </div>

    <section
      dir="ltr"
      class="panel-glass overflow-hidden rounded-[1.75rem] p-3 sm:rounded-[2rem] sm:p-4 md:p-5"
    >
      <div class="panel-glass-content min-w-0">
        <LiquidityChart
          :key="chartKey"
          :exchange="exchange"
          :symbol="symbol"
          :timeframe="timeframe"
          :levels="levels"
          :api-base="apiBase"
          :height="chartHeight"
          :refresh-ms="props.refreshMs || POLL_MS"
          @block="blockZone"
        />
      </div>
    </section>

    <section class="panel-glass rounded-[1.75rem] p-4 sm:rounded-[2rem] sm:p-5">
      <div class="panel-glass-content">
        <header class="mb-4 flex items-baseline justify-between gap-3">
          <h2 class="text-sm font-semibold text-zinc-900 dark:text-white">Price zones</h2>
          <span class="font-mono text-[11px] text-zinc-400">{{ levelRows.length }} levels</span>
        </header>
        <p v-if="!levelRows.length" class="text-sm text-zinc-500">
          No zones at this threshold.
        </p>
        <div v-else class="panel-scrollbar max-h-72 overflow-y-auto">
          <table class="w-full text-left text-xs sm:text-sm">
            <thead class="text-[10px] uppercase tracking-wider text-zinc-400">
              <tr>
                <th class="pb-2 pe-3">Side</th>
                <th class="pb-2 pe-3">Price</th>
                <th class="pb-2 pe-3">TF</th>
                <th class="pb-2 pe-3">Strength</th>
                <th class="pb-2 pe-3">Distance</th>
                <th class="pb-2 text-end">{{ __('Action') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, i) in levelRows"
                :key="`${row.timeframe}-${row.side}-${row.price}-${i}`"
                class="border-t border-zinc-200/50 dark:border-zinc-700/50"
                :class="{ 'opacity-60': row.blocked }"
              >
                <td
                  class="py-2 pe-3 font-medium"
                  :class="row.side === 'short' ? 'text-rose-500' : 'text-emerald-500'"
                >
                  {{ row.side === 'short' ? 'Short' : 'Long' }}
                </td>
                <td class="py-2 pe-3 font-mono tabular-nums">
                  <span :class="{ 'line-through text-zinc-400': row.blocked }">
                    {{ levelPillText(row.timeframe, row.price) }}
                  </span>
                  <span
                    v-if="row.blocked"
                    class="ms-2 inline-flex rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400"
                  >
                    {{ __('Blocked') }}
                  </span>
                </td>
                <td class="py-2 pe-3 font-mono text-[11px] text-zinc-500">{{ row.timeframe }}</td>
                <td class="py-2 pe-3 font-mono tabular-nums text-zinc-500">{{ row.pct_of_max?.toFixed(0) }}%</td>
                <td class="py-2 pe-3 font-mono tabular-nums text-zinc-500">
                  {{ formatDistance(row.distance_pct) }}
                </td>
                <td class="py-2 text-end">
                  <button
                    v-if="row.blocked"
                    type="button"
                    class="rounded-full border border-zinc-200 px-2.5 py-1 text-[11px] font-semibold text-zinc-600 hover:bg-zinc-50 disabled:opacity-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
                    :disabled="zoneBusy"
                    @click="unblockZone(row)"
                  >
                    {{ __('Unblock') }}
                  </button>
                  <button
                    v-else
                    type="button"
                    class="rounded-full border border-rose-200 px-2.5 py-1 text-[11px] font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-50 dark:border-rose-900/60 dark:text-rose-300 dark:hover:bg-rose-950/40"
                    :disabled="zoneBusy"
                    @click="blockZone(row)"
                  >
                    {{ __('Block') }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <LiquidityUpdateModal
      :key="updateNoticeKey"
      :open="showUpdateModal"
      :symbol="symbol"
      @close="showUpdateModal = false"
    />
  </div>
</template>
